import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { Building, CheckCircle, Shield, Users } from "lucide-react";
import RegisterForm from "@/components/auth/register/RegisterForm";
import LoginPrompt from "@/components/auth/common/LoginPrompt";
import { useAuth } from "@/components/auth/AuthProvider";

const benefits = [
  {
    icon: Building,
    title: "Manage Your Profile",
    description: "Update your nursing home's information, photos, and amenities",
  },
  {
    icon: Users,
    title: "Connect with Families",
    description: "Receive and manage enquiries from families looking for care",
  },
  {
    icon: Shield,
    title: "Verified Badge",
    description: "Get a trust badge on your listing to stand out from unclaimed profiles",
  },
  {
    icon: CheckCircle,
    title: "Analytics & Insights",
    description: "Track how families interact with your listing",
  },
];

export default function OwnerRegisterPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      navigate("/owner/claim");
    }
  }, [user, navigate]);

  // Pre-set session context so post-signup redirect works correctly
  useEffect(() => {
    sessionStorage.setItem("userType", "owner");
  }, []);

  return (
    <Layout hideFooter>
      <Helmet>
        <title>Register as a Care Provider | Grandpal</title>
        <meta
          name="description"
          content="Create your nursing home provider account on Grandpal to claim your profile, manage your listing, and connect with families."
        />
      </Helmet>

      <div className="min-h-[calc(100vh-80px)] bg-muted/30">
        <div className="container max-w-6xl py-8 md:py-12 px-4">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            {/* Left: Benefits */}
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                  Grow your nursing home's visibility
                </h1>
                <p className="mt-3 text-lg text-muted-foreground">
                  Join Grandpal to manage your listing, connect with families
                  searching for care, and showcase what makes your facility
                  special.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((benefit) => {
                  const Icon = benefit.icon;
                  return (
                    <motion.div
                      key={benefit.title}
                      className="flex gap-3 p-4 rounded-lg bg-background border"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.2 }}
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm text-foreground">
                          {benefit.title}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {benefit.description}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              <p className="text-xs text-muted-foreground">
                Already have an account?{" "}
                <button
                  onClick={() => navigate("/auth/login?userType=provider&redirectTo=/claim")}
                  className="text-primary font-medium hover:underline"
                >
                  Sign in and claim your profile
                </button>
              </p>
            </motion.div>

            {/* Right: Registration form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card className="p-6 md:p-8 bg-background shadow-lg border">
                <RegisterForm fromClaim />
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
